import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { FormInput, registerObject } from "go-form";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import AttachmentsForm from "../../../../../../../../../../components/Attachment/AttachmentsForm";
import {
	TagsFormInput,
	mapTagsToAcceptableByBackendFormat,
} from "../../../../../../../../../../components/Tag/TagsFormInput";
import {
	TaxApi,
	TransferApi,
	TransferItemApi,
	TransferStatus,
} from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import { SearchSelectApi } from "../../../../../../../../../../services/Api/types";
import ItemsError from "../../../../components/ItemsError";
import TransferFormItemsTable from "./TransferFormItemsTable";

interface Props {
	transfer?: TransferApi;
	customFieldsConfig?: CustomFieldTemplateApi[];
	fetchTransfer?: () => void;
}

const TransferForm = ({ customFieldsConfig, transfer, fetchTransfer }: Props) => {
	const form = useForm<TransferApi>({
		criteriaMode: "all",
		defaultValues: {
			...transfer,
			transferred_at: transfer?.transferred_at ?? formatDateToString(new Date()),
			items: !transfer?.items
				? [{ position: 0 } as TransferItemApi]
				: [...transfer.items, { position: transfer.items.length }],
			custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], transfer?.custom_fields || []),
		},
	});
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		handleSubmit,
		formState,
		clearErrors,
		formState: { errors },
		register,
		control,
		setValue,
		setError,
	} = form;
	const { t } = useTranslation();
	const [loading, setLoading] = useState<boolean>(false);
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const [formErrors, setFormErrors] = useState<ApiError[]>([]);
	const [taxes, setTaxes] = useState<TaxApi[] | undefined>(undefined);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	useEffect(() => {
		const fetchTaxes = async () => {
			try {
				const res = await api.organization().getTaxes();
				setTaxes(res);
			} catch (err) {
				handleError.alert(err, addFlash);
			}
		};
		fetchTaxes();
	}, []);

	const onSubmit = handleSubmit(async (data: TransferApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		let res;
		data.items = [...data.items.filter((item) => item?.volume || item?.product?.name || item?.quantity)];
		if (data.tags) data.tags = mapTagsToAcceptableByBackendFormat(data.tags);
		try {
			if (transfer?.id) {
				res = await api.organization().updateTransfer(data);
			} else {
				res = await api.organization().createTransfer(data);
			}

			history.push(`/${organization.id}/documents/transfers/${res.id}`);
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.form(err, setError, addFlash, ["items"]);
			const exceptionErrors = handleException(err);
			setFormErrors([...exceptionErrors]);
		}
		setLoading(false);
	});

	const onSearchStocks = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getStockSearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const onRegisterDestinationStock = (stock?: SearchSelectApi) => {
		registerObject(register, "destination_stock", ["name"]);
		setValue("destination_stock.name", stock ? stock.label : "");
	};

	const onRegisterSourceStock = (stock?: SearchSelectApi) => {
		registerObject(register, "source_stock", ["name"]);
		setValue("source_stock.name", stock ? stock.label : "");
	};

	const checkIfStockInputIsDisabled = () => {
		if (!transfer) return false;
		if (!transfer.status) return false;
		return !(transfer?.status === TransferStatus.DRAFT || transfer?.status === TransferStatus.VOID);
	};

	const transferredAtValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const sourceStockValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const destinationStockValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	if (!taxes) return <LoadingContainer />;

	return (
		<FormDirty
			formState={formState}
			key="transfer-form"
			loading={loading}
			onSubmit={(data) => {
				clearErrors();
				onSubmit(data);
			}}
			noValidate
		>
			<h5>{t("common.word.basic_info")}</h5>
			<fieldset className="form-group">
				<div className="row">
					<div className="col-lg-10">
						<div className="row">
							<div className="col-md-6">
								<FormInput
									name="reference_number"
									errors={errors}
									register={register}
									label={t("common.word.reference_number")}
								/>
							</div>
							<div className="col-md-6">
								<FormDatePicker
									errors={errors}
									control={control}
									name="transferred_at"
									allowClear={false}
									label={t("modules.purchase.field.transferred_at.title")}
									customValidationConfig={transferredAtValidationConfig}
								/>
							</div>
						</div>
						<div className="row">
							<div className="col-md-6">
								<FormSelectGroup
									control={control}
									loadOptions={onSearchStocks}
									label={t("modules.transfer.field.from_stock.title")}
									name="source_stock_id"
									onChange={(id, stock) => onRegisterSourceStock(stock)}
									getOptionValue={(opt) => opt.id}
									defaultValue={{
										label: transfer?.source_stock?.name,
										id: transfer?.source_stock?.id,
									}}
									disabled={checkIfStockInputIsDisabled()}
									errors={errors}
									customValidationConfig={sourceStockValidationConfig}
									data-testid="source_stock_id"
								/>
							</div>
							<div className="col-md-6">
								<FormSelectGroup
									control={control}
									loadOptions={onSearchStocks}
									label={t("modules.transfer.field.to_stock.title")}
									name="destination_stock_id"
									onChange={(id, stock) => onRegisterDestinationStock(stock)}
									disabled={checkIfStockInputIsDisabled()}
									getOptionValue={(opt) => opt.id}
									defaultValue={{
										label: transfer?.destination_stock?.name,
										id: transfer?.destination_stock?.id,
									}}
									errors={errors}
									customValidationConfig={destinationStockValidationConfig}
									data-testid="destination_stock_id"
								/>
							</div>
						</div>
						<div className="row">
							<div className="col-md-6">
								<FormInput
									name="comment"
									errors={errors}
									register={register}
									label={t("common.word.comment.title")}
								/>
							</div>
						</div>
					</div>
				</div>
			</fieldset>
			{customFieldsConfig && customFieldsConfig.length > 0 && (
				<fieldset className="form-group">
					<h5>{t("common.word.custom_fields")}</h5>
					<CustomFieldsForm
						customFieldsConfig={customFieldsConfig}
						customFields={transfer?.custom_fields || []}
						form={form}
						setErrors={setErrors}
					/>
				</fieldset>
			)}
			<ItemsError
				apiErrors={formErrors}
				formErrors={formState.errors}
				register={form.register}
				watch={form.watch}
				setErrors={setErrors}
			/>
			<TransferFormItemsTable form={form} taxes={taxes} fetchTransfer={fetchTransfer} />
			<h5>{t("lib:common.word.advanced")}</h5>
			<div>
				<TagsFormInput form={form} />
				<span className="mt-4 d-block">{t("common.word.attachments")}</span>
				<AttachmentsForm form={form} />
			</div>
		</FormDirty>
	);
};

export default TransferForm;
